import { useMemo } from "react";

import { Pill } from "../../components/Pill";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/Popover";
import {
  type ApiBuildStatus,
  useTaskBuildStatuses,
} from "../../data/api/buildStatuses";
import { Icon } from "../../icons";
import {
  CHECKS_LABEL,
  checksColorVar,
  type ChecksState,
} from "../../lib/checks";

// The Pill tone each state reads in. The words and the dot colour are the
// board's too (`lib/checks.ts`), so a row's dot and the pill it opens cannot
// come out in different colours.
const PILL_STATUS: Record<ChecksState, "done" | "needs-input" | "pending"> = {
  failing: "needs-input",
  passing: "done",
  pending: "pending",
};

// Rows are listed worst-first, so whatever is broken is the first thing read.
const ORDER = ["failing", "pending", "passing"] as const;

const severity = (s: ApiBuildStatus): ChecksState => {
  const v = (s.conclusion ?? s.state).toLowerCase();
  if (["neutral", "skipped", "success"].includes(v)) {
    return "passing";
  }
  if (
    [
      "in_progress",
      "pending",
      "queued",
      "requested",
      "waiting",
      "expected",
    ].includes(s.state.toLowerCase())
  ) {
    return "pending";
  }
  return "failing";
};

export const BuildStatusPill = ({ taskId }: { readonly taskId: string }) => {
  const { statuses, summary } = useTaskBuildStatuses(taskId);
  // Stable within a severity, so contexts keep the provider's own order.
  const ordered = useMemo(
    () =>
      [...statuses].sort(
        (a, b) => ORDER.indexOf(severity(a)) - ORDER.indexOf(severity(b)),
      ),
    [statuses],
  );
  if (summary === "none") {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          style={{
            background: "none",
            border: 0,
            cursor: "pointer",
            padding: 0,
          }}
          title="Build checks"
          type="button"
        >
          <Pill status={PILL_STATUS[summary]}>
            {CHECKS_LABEL[summary]} · {statuses.length}
          </Pill>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6}>
        <div
          style={{
            maxHeight: 320,
            minWidth: 280,
            overflow: "auto",
            padding: 6,
          }}
        >
          {ordered.map((s) => (
            <div
              key={s.public_id}
              style={{
                alignItems: "center",
                display: "flex",
                gap: 9,
                padding: "7px 8px",
              }}
            >
              <span
                style={{
                  background: checksColorVar(severity(s)),
                  borderRadius: "50%",
                  flex: "none",
                  height: 8,
                  width: 8,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ color: "var(--ink)", fontSize: 13, fontWeight: 500 }}
                >
                  {s.context}
                </div>
                <div style={{ color: "var(--ink-3)", fontSize: 12 }}>
                  {s.conclusion ?? s.state}
                  {s.summary ? ` · ${s.summary}` : ""}
                </div>
              </div>
              {s.details_url ? (
                <a
                  href={s.details_url}
                  rel="noreferrer"
                  style={{ color: "var(--ink-3)", flex: "none" }}
                  target="_blank"
                  title="Open details"
                >
                  <Icon.External size={13} />
                </a>
              ) : null}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
